// xlsx-js-style is an API-compatible fork of SheetJS that ACTUALLY writes cell
// styles (fills/fonts). The community `xlsx` build silently drops `.s` styles on
// write, so header colors never rendered with it.
import * as XLSX from 'xlsx-js-style';

export type ExportFormat = 'csv' | 'xlsx' | 'pdf';

export interface ExportColumn<T> {
  key: keyof T | string;
  header: string;
  formatter?: (value: unknown, row: T) => string | number;
}

/**
 * A secondary table included in the same export alongside the primary one.
 * In XLSX each becomes its own worksheet; in CSV/PDF each is appended as a
 * separate titled section.
 */
export interface ExportTable<T = Record<string, unknown>> {
  sheetName: string;
  /** Optional one-line explanation shown under the table's title. */
  description?: string;
  columns: ExportColumn<T>[];
  data: T[];
}

export interface ExportOptions<T> {
  filename: string;
  format: ExportFormat;
  columns: ExportColumn<T>[];
  data: T[];
  sheetName?: string;
  /**
   * Optional additional tables to include in the SAME export alongside the
   * primary table above. When omitted, the export is a single table and its
   * output is unchanged.
   */
  extraSheets?: ExportTable<any>[];
  /**
   * Optional table-name title rendered above the PRIMARY table (a big title row
   * in XLSX, a leading line in CSV; PDF already titles the primary with the
   * sheet name). Omit to keep the plain single-table output unchanged.
   */
  primaryTitle?: string;
  /** Optional one-line explanation shown under the primary table's title. */
  primaryDescription?: string;
  /**
   * PDF only: a window opened synchronously by the caller (within the click
   * gesture) to write the printable HTML into. Passing it avoids the popup
   * blocker that fires when `window.open` runs after an `await` (e.g. an async
   * data fetch). When omitted, exportToPdf opens its own window (unchanged).
   */
  pdfWindow?: Window | null;
}

/**
 * Get value from object by path (supports nested paths like 'user.name')
 */
function getValueByPath(obj: Record<string, unknown>, path: string): unknown {
  return path.split('.').reduce((acc: unknown, part: string) => {
    if (acc && typeof acc === 'object') {
      return (acc as Record<string, unknown>)[part];
    }
    return undefined;
  }, obj);
}

/**
 * Convert data to a 2D array for export
 */
function dataToRows<T>(
  data: T[],
  columns: ExportColumn<T>[]
): (string | number)[][] {
  const headers = columns.map((col) => col.header);

  const rows = data.map((row) =>
    columns.map((col) => {
      const value = getValueByPath(row as Record<string, unknown>, col.key as string);

      if (col.formatter) {
        return col.formatter(value, row);
      }

      if (value === null || value === undefined) {
        return '';
      }

      if (value instanceof Date) {
        return value.toISOString();
      }

      if (typeof value === 'object') {
        return JSON.stringify(value);
      }

      return value as string | number;
    })
  );

  return [headers, ...rows];
}

/**
 * Serialize one table (headers + rows) to CSV text.
 */
function tableToCsv<T>(data: T[], columns: ExportColumn<T>[]): string {
  const rows = dataToRows(data, columns);

  return rows
    .map((row) =>
      row
        .map((cell) => {
          const cellStr = String(cell);
          // Escape quotes and wrap in quotes if contains comma, newline, or quote
          if (cellStr.includes(',') || cellStr.includes('\n') || cellStr.includes('"')) {
            return `"${cellStr.replace(/"/g, '""')}"`;
          }
          return cellStr;
        })
        .join(',')
    )
    .join('\n');
}

/** Escape a single free-text value (title/description) as one CSV field. */
function csvCell(value: string): string {
  return value.includes(',') || value.includes('\n') || value.includes('"')
    ? `"${value.replace(/"/g, '""')}"`
    : value;
}

/**
 * Export data as CSV
 */
function exportToCsv<T>(options: ExportOptions<T>): void {
  const sections: string[] = [];
  // Optional table-name title (+ description) above the primary table, then a
  // blank gap line before the header. (CSV is plain text \u2014 no colour/font.)
  if (options.primaryTitle) {
    sections.push(csvCell(options.primaryTitle));
    if (options.primaryDescription) sections.push(csvCell(options.primaryDescription));
    sections.push(''); // gap row between the title and the header
  }
  sections.push(tableToCsv(options.data, options.columns));

  // Append each extra table as a blank-line-separated, titled section so a
  // single CSV file can carry several tables.
  (options.extraSheets || []).forEach((table) => {
    sections.push('', csvCell(table.sheetName));
    if (table.description) sections.push(csvCell(table.description));
    sections.push(''); // gap row between the title and the header
    sections.push(tableToCsv(table.data, table.columns));
  });

  const csvContent = sections.join('\n');

  const blob = new Blob(['\ufeff' + csvContent], { type: 'text/csv;charset=utf-8;' });
  downloadBlob(blob, `${options.filename}.csv`);
}

// Header cell style shared by every export table: a solid 729FCF (blue) fill
// with bold white text. Applied to each table's header row (single-table path).
const HEADER_CELL_STYLE = {
  font: { bold: true, color: { rgb: 'FFFFFF' } },
  fill: { patternType: 'solid', fgColor: { rgb: '729FCF' } },
} as const;

// Stacked (multi-table) export variants — column headers are right-aligned.
const HEADER_CELL_STYLE_RIGHT = {
  font: { bold: true, color: { rgb: 'FFFFFF' } },
  fill: { patternType: 'solid', fgColor: { rgb: '729FCF' } },
  alignment: { horizontal: 'right' },
} as const;

// Section-title row style — larger, bold, on the same 729FCF band.
const TITLE_CELL_STYLE = {
  font: { bold: true, sz: 18, color: { rgb: 'FFFFFF' } },
  fill: { patternType: 'solid', fgColor: { rgb: '729FCF' } },
} as const;

// Description row style — small italic grey explanatory text (no fill).
const DESC_CELL_STYLE = {
  font: { italic: true, sz: 10, color: { rgb: '555555' } },
} as const;

/**
 * Build a styled, auto-sized worksheet for one table.
 */
function buildWorksheet<T>(columns: ExportColumn<T>[], data: T[]): XLSX.WorkSheet {
  const rows = dataToRows(data, columns);

  const worksheet = XLSX.utils.aoa_to_sheet(rows);

  // Auto-size columns
  const colWidths = columns.map((col, idx) => {
    const headerWidth = col.header.length;
    const maxDataWidth = Math.max(
      ...rows.slice(1).map((row) => String(row[idx]).length)
    );
    return { wch: Math.min(Math.max(headerWidth, maxDataWidth) + 2, 50) };
  });
  worksheet['!cols'] = colWidths;

  // Style header row
  const headerRange = XLSX.utils.decode_range(worksheet['!ref'] || 'A1');
  for (let col = headerRange.s.c; col <= headerRange.e.c; col++) {
    const cellRef = XLSX.utils.encode_cell({ r: 0, c: col });
    if (worksheet[cellRef]) {
      worksheet[cellRef].s = HEADER_CELL_STYLE;
    }
  }

  return worksheet;
}

/** Apply a cell style across `colCount` cells of a worksheet row. */
function styleRow(
  worksheet: XLSX.WorkSheet,
  rowIdx: number,
  colCount: number,
  style: unknown,
): void {
  for (let c = 0; c < colCount; c++) {
    const cellRef = XLSX.utils.encode_cell({ r: rowIdx, c });
    if (worksheet[cellRef]) {
      worksheet[cellRef].s = style;
    }
  }
}

/** Auto-size columns from the widest cell (incl. header) in each column. */
function colWidthsForRows(rows: (string | number)[][]): { wch: number }[] {
  const widths: number[] = [];
  rows.forEach((row) => {
    row.forEach((cell, idx) => {
      widths[idx] = Math.max(widths[idx] || 0, String(cell ?? '').length);
    });
  });
  return widths.map((w) => ({ wch: Math.min(w + 2, 50) }));
}

/**
 * Export data as Excel (XLSX)
 */
function exportToXlsx<T>(options: ExportOptions<T>): void {
  const workbook = XLSX.utils.book_new();
  const hasExtras = !!options.extraSheets?.length;
  const hasTitle = !!options.primaryTitle;

  if (!hasExtras && !hasTitle) {
    // Plain single-table export — unchanged behavior (used by other features).
    const worksheet = buildWorksheet(options.columns, options.data);
    XLSX.utils.book_append_sheet(workbook, worksheet, options.sheetName || 'Data');
  } else {
    // Titled / multi-table export — stack into ONE worksheet, top-to-bottom.
    // Each table is laid out as:
    //   [big title row] [blank gap] [column header] [data…]
    // and extra tables get a blank separator row before their title.
    const aoa: (string | number)[][] = [];
    // Column-header rows (right-aligned blue), section-title rows (big blue),
    // and description rows (small italic grey).
    const headerRows: Array<{ row: number; cols: number }> = [];
    const titleRows: Array<{ row: number; cols: number }> = [];
    const descRows: number[] = [];

    // One table's block: [title][description?][gap][header][data…].
    const pushTable = (
      title: string | undefined,
      description: string | undefined,
      columns: ExportColumn<any>[],
      data: any[],
    ) => {
      const cols = columns.length;
      if (title) {
        titleRows.push({ row: aoa.length, cols });
        aoa.push([title, ...Array(Math.max(0, cols - 1)).fill('')]);
        if (description) {
          descRows.push(aoa.length);
          aoa.push([description]);
        }
        aoa.push([]); // gap between the table name and its header row
      }
      headerRows.push({ row: aoa.length, cols });
      dataToRows(data, columns).forEach((r) => aoa.push(r));
    };

    pushTable(options.primaryTitle, options.primaryDescription, options.columns, options.data);

    (options.extraSheets || []).forEach((table) => {
      aoa.push([]); // blank separator row before the section
      pushTable(table.sheetName, table.description, table.columns, table.data);
    });

    const worksheet = XLSX.utils.aoa_to_sheet(aoa);
    worksheet['!cols'] = colWidthsForRows(aoa);
    headerRows.forEach(({ row, cols }) => styleRow(worksheet, row, cols, HEADER_CELL_STYLE_RIGHT));
    titleRows.forEach(({ row, cols }) => styleRow(worksheet, row, cols, TITLE_CELL_STYLE));
    descRows.forEach((row) => styleRow(worksheet, row, 1, DESC_CELL_STYLE));

    XLSX.utils.book_append_sheet(workbook, worksheet, options.sheetName || 'Data');
  }

  const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
  const blob = new Blob([excelBuffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });

  downloadBlob(blob, `${options.filename}.xlsx`);
}

/**
 * Download a blob as a file
 */
function downloadBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

/**
 * Render one table as an `<h2>` heading + HTML `<table>` for the PDF document.
 */
function renderPdfSection<T>(title: string, data: T[], columns: ExportColumn<T>[]): string {
  const rows = dataToRows(data, columns);
  const headers = rows[0] || [];
  const dataRows = rows.slice(1);
  const colCount = headers.length || 1;
  const colWidth = Math.floor(100 / colCount);

  let html = `<h2 style="margin-top:24px">${title}</h2><table><thead><tr>`;
  headers.forEach((h) => {
    html += `<th style="width:${colWidth}%">${h}</th>`;
  });
  html += '</tr></thead><tbody>';

  dataRows.forEach((row) => {
    html += '<tr>';
    row.forEach((cell) => {
      html += `<td>${String(cell).replace(/</g, '&lt;').replace(/>/g, '&gt;')}</td>`;
    });
    html += '</tr>';
  });

  html += '</tbody></table>';
  return html;
}

/**
 * Export data as PDF (table format)
 */
function exportToPdf<T>(options: ExportOptions<T>): void {
  const rows = dataToRows(options.data, options.columns);
  const headers = rows[0];
  const dataRows = rows.slice(1);

  // Build simple HTML table for PDF
  const colCount = headers.length;
  const colWidth = Math.floor(100 / colCount);

  let html = `
    <html><head><style>
      body { font-family: Arial, sans-serif; margin: 20px; }
      h2 { color: #333; margin-bottom: 4px; }
      .subtitle { color: #666; font-size: 12px; margin-bottom: 16px; }
      table { width: 100%; border-collapse: collapse; font-size: 11px; }
      th { background: #729FCF; color: #ffffff; padding: 8px 6px; text-align: left; border: 1px solid #5b86b3; font-weight: 700; }
      td { padding: 6px; border: 1px solid #e5e7eb; }
      tr:nth-child(even) { background: #f9fafb; }
      .footer { margin-top: 16px; font-size: 10px; color: #9ca3af; }
    </style></head><body>
    <h2>${options.sheetName || 'Report'}</h2>
    <div class="subtitle">Generated: ${new Date().toLocaleString()}</div>
    <table><thead><tr>`;

  headers.forEach((h) => {
    html += `<th style="width:${colWidth}%">${h}</th>`;
  });
  html += '</tr></thead><tbody>';

  dataRows.forEach((row) => {
    html += '<tr>';
    row.forEach((cell) => {
      html += `<td>${String(cell).replace(/</g, '&lt;').replace(/>/g, '&gt;')}</td>`;
    });
    html += '</tr>';
  });

  html += '</tbody></table>';

  // Append any extra tables as their own titled sections.
  (options.extraSheets || []).forEach((table) => {
    html += renderPdfSection(table.sheetName, table.data, table.columns);
  });

  const totalRows =
    options.data.length +
    (options.extraSheets || []).reduce((sum, table) => sum + table.data.length, 0);
  html += `<div class="footer">${totalRows} rows exported</div>`;
  html += '</body></html>';

  // Prefer a window the caller opened synchronously (popup-blocker-safe when the
  // export awaited async data); otherwise open one now (unchanged fallback).
  const printWindow = options.pdfWindow ?? window.open('', '_blank');
  if (printWindow) {
    // Reset the (possibly pre-opened about:blank) document, then write content.
    printWindow.document.open();
    printWindow.document.write(html);
    printWindow.document.close();
    printWindow.focus();
    // Trigger print after layout. Not relying on `onload` — it won't re-fire for
    // a pre-opened window that already loaded about:blank.
    setTimeout(() => {
      try {
        printWindow.print();
      } catch {
        /* user closed the window before printing */
      }
    }, 400);
  }
}

/**
 * Export data to CSV, Excel, or PDF format
 */
export function exportData<T>(options: ExportOptions<T>): void {
  if (options.format === 'csv') {
    exportToCsv(options);
  } else if (options.format === 'pdf') {
    exportToPdf(options);
  } else {
    exportToXlsx(options);
  }
}

/**
 * Pre-defined column formatters
 */
export const formatters = {
  date: (value: unknown): string => {
    if (!value) return '';
    const date = value instanceof Date ? value : new Date(value as string);
    return date.toLocaleDateString();
  },

  dateTime: (value: unknown): string => {
    if (!value) return '';
    const date = value instanceof Date ? value : new Date(value as string);
    return date.toLocaleString();
  },

  currency: (value: unknown, currency = 'USD'): string => {
    if (value === null || value === undefined) return '';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency,
    }).format(value as number);
  },

  percentage: (value: unknown): string => {
    if (value === null || value === undefined) return '';
    return `${(value as number).toFixed(2)}%`;
  },

  boolean: (value: unknown): string => {
    return value ? 'Yes' : 'No';
  },

  array: (value: unknown): string => {
    if (!Array.isArray(value)) return '';
    return value.join(', ');
  },
};

/**
 * Generate filename with current date
 */
export function generateExportFilename(prefix: string): string {
  const now = new Date();
  const dateStr = now.toISOString().split('T')[0];
  return `${prefix}_${dateStr}`;
}
